import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
    Shield,
    Clock,
    CheckCircle2,
    AlertTriangle,
    Settings,
    Trash2,
    Zap,
    FileText,
    Calendar,
    TrendingUp,
    Timer,
    Bell,
    Mail,
    MessageSquare,
    Users,
} from 'lucide-react'
import { Typogaphy, Dialog, Button as VButton } from 'vnetwork-ui'

import Paper from '@/components/Paper'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useConfirmation } from '@/contexts/confirmation'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getServiceSLA,
    getActiveSLATemplates,
    applyTemplateToService,
    deleteServiceSLA,
} from '@/modules/Service/services/sla'
import {
    SLA_SOURCE_LABELS,
    SLA_TIER_CONFIG,
    SLATier,
    SLATemplate,
} from '@/modules/Service/types/sla'
import { generatePathDatacentral, paths } from '@/utils/path'

interface ServiceSLAConfigProps {
    serviceId: number
}

// Template card for selection
const TemplateCard: React.FC<{
    template: SLATemplate
    isSelected: boolean
    onSelect: () => void
}> = ({ template, isSelected, onSelect }) => {
    const tier =
        template.template_tier ||
        ('custom' as SLATier as Exclude<SLATier, 'custom'>)
    const tierConfig = tier && tier !== 'custom' ? SLA_TIER_CONFIG[tier] : null

    return (
        <Card
            className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected
                    ? 'ring-2 ring-primary border-primary bg-primary/5'
                    : 'hover:border-gray-300'
            }`}
            onClick={onSelect}
        >
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Shield
                            className="w-5 h-5"
                            style={{ color: tierConfig?.color || '#6B7280' }}
                        />
                        <CardTitle className="text-base">
                            {template.template_name}
                        </CardTitle>
                    </div>
                    <Badge
                        style={{
                            backgroundColor: `${tierConfig?.color}20`,
                            color: tierConfig?.color,
                            borderColor: tierConfig?.color,
                        }}
                    >
                        {tier.toUpperCase()}
                    </Badge>
                </div>
                {template.template_description && (
                    <CardDescription className="text-xs line-clamp-2">
                        {template.template_description}
                    </CardDescription>
                )}
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1.5 text-gray-600">
                        <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                        <span>{template.uptime_guarantee}% uptime</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-600">
                        <Clock className="w-3.5 h-3.5 text-blue-500" />
                        <span>{template.default_response_time}m response</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-600">
                        <Timer className="w-3.5 h-3.5 text-purple-500" />
                        <span>{template.default_resolution_time}m resolve</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-600">
                        <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                        <span>
                            {template.downtime_allowance_minutes}m/month
                        </span>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

const ServiceSLAConfig: React.FC<ServiceSLAConfigProps> = ({ serviceId }) => {
    const { t } = useTranslation('services')
    const { setConfirmation } = useConfirmation()
    const navigate = useNavigate()

    const [isApplyTemplateOpen, setIsApplyTemplateOpen] = useState(false)
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
        null
    )

    // Navigate to SLA config page
    const goToSLAConfig = () => {
        navigate(
            generatePathDatacentral(paths.serviceSLAConfig, { id: serviceId })
        )
    }

    // Fetch current SLA
    const {
        data: slaData,
        isLoading,
        refetch,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICE_SLA, serviceId],
        queryFn: () => getServiceSLA(serviceId),
    })

    // Fetch available templates
    const { data: templatesData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SLA_TEMPLATES],
        queryFn: getActiveSLATemplates,
    })

    // Apply template mutation
    const { mutate: applyTemplate, isPending: isApplying } =
        useMutationWithHandler(
            {
                mutationFn: (templateId: number) =>
                    applyTemplateToService(serviceId, {
                        sla_template_id: templateId,
                    }),
            },
            {
                successMessage: t(
                    'sla.template_applied',
                    'Đã áp dụng template SLA'
                ),
                reactQueryKeys: QUERY_KEYS.SERVICE_SLA,
                onSuccess: () => {
                    setIsApplyTemplateOpen(false)
                    setSelectedTemplateId(null)
                    refetch()
                },
            }
        )

    // Delete SLA mutation
    const { mutate: deleteSLA } = useMutationWithHandler(
        {
            mutationFn: () => deleteServiceSLA(serviceId),
        },
        {
            successMessage: t('sla.sla_removed', 'Đã xóa cấu hình SLA'),
            reactQueryKeys: QUERY_KEYS.SERVICE_SLA,
            onSuccess: () => refetch(),
        }
    )

    const sla = slaData?.result
    const templates = templatesData || []

    const handleApplyTemplate = () => {
        if (selectedTemplateId) {
            applyTemplate(selectedTemplateId)
        }
    }

    const handleDelete = () => {
        setConfirmation({
            open: true,
            title: t('sla.remove_sla_title', 'Xóa cấu hình SLA'),
            description: t(
                'sla.remove_sla_description',
                'Bạn có chắc muốn xóa cấu hình SLA cho service này?'
            ),
            onConfirm: () => deleteSLA(),
        })
    }

    if (isLoading) {
        return (
            <Paper className="p-6">
                <div className="flex items-center gap-3 mb-6">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div>
                        <Skeleton className="h-5 w-32 mb-1" />
                        <Skeleton className="h-4 w-24" />
                    </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-24 rounded-lg" />
                    ))}
                </div>
            </Paper>
        )
    }

    // No SLA configured - show setup options
    if (!sla) {
        return (
            <Paper className="p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2.5 bg-gray-100 rounded-lg">
                        <Shield className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                        <Typogaphy className="font-semibold text-gray-900">
                            {t('sla.service_sla', 'Service SLA')}
                        </Typogaphy>
                        <p className="text-sm text-gray-500">
                            {t(
                                'sla.no_sla_configured',
                                'Chưa có cấu hình SLA cho service này'
                            )}
                        </p>
                    </div>
                </div>

                {/* Setup Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Apply Template Option */}
                    <Card
                        className="cursor-pointer transition-all hover:shadow-md hover:border-primary group"
                        onClick={() => setIsApplyTemplateOpen(true)}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                                    <Zap className="w-6 h-6 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 mb-1">
                                        {t(
                                            'sla.apply_template',
                                            'Áp dụng Template'
                                        )}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        Chọn từ các template SLA có sẵn (Bronze,
                                        Silver, Gold, Platinum)
                                    </p>
                                    <div className="flex items-center gap-2 mt-3">
                                        <Badge
                                            variant="outline"
                                            className="text-xs"
                                        >
                                            Nhanh chóng
                                        </Badge>
                                        <Badge
                                            variant="outline"
                                            className="text-xs"
                                        >
                                            Chuẩn hóa
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Manual Config Option */}
                    <Card
                        className="cursor-pointer transition-all hover:shadow-md hover:border-primary group"
                        onClick={goToSLAConfig}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors">
                                    <Settings className="w-6 h-6 text-purple-600" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 mb-1">
                                        {t(
                                            'sla.manual_config',
                                            'Cấu hình thủ công'
                                        )}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        Tùy chỉnh các thông số SLA theo yêu cầu
                                        riêng của khách hàng
                                    </p>
                                    <div className="flex items-center gap-2 mt-3">
                                        <Badge
                                            variant="outline"
                                            className="text-xs"
                                        >
                                            Linh hoạt
                                        </Badge>
                                        <Badge
                                            variant="outline"
                                            className="text-xs"
                                        >
                                            Tùy chỉnh
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Apply Template Dialog */}
                <Dialog
                    open={isApplyTemplateOpen}
                    onClose={() => {
                        setIsApplyTemplateOpen(false)
                        setSelectedTemplateId(null)
                    }}
                    title={t(
                        'sla.apply_template_title',
                        'Áp dụng Template SLA'
                    )}
                    actions={
                        <div className="flex items-center gap-2">
                            <VButton
                                variant="outlined"
                                onClick={() => {
                                    setIsApplyTemplateOpen(false)
                                    setSelectedTemplateId(null)
                                }}
                            >
                                {t('common.cancel', 'Hủy')}
                            </VButton>
                            <VButton
                                onClick={handleApplyTemplate}
                                disabled={!selectedTemplateId}
                                loading={isApplying}
                            >
                                {t('sla.apply_template', 'Áp dụng')}
                            </VButton>
                        </div>
                    }
                    className="max-w-2xl"
                >
                    <p className="text-sm text-gray-500 mb-4">
                        {t(
                            'sla.apply_template_description',
                            'Chọn template SLA để áp dụng cho service này'
                        )}
                    </p>

                    {templates.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
                            {templates.map((template) => (
                                <TemplateCard
                                    key={template.sla_template_id}
                                    template={template}
                                    isSelected={
                                        selectedTemplateId ===
                                        template.sla_template_id
                                    }
                                    onSelect={() =>
                                        setSelectedTemplateId(
                                            template.sla_template_id
                                        )
                                    }
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-500">
                                {t(
                                    'sla.no_templates',
                                    'Chưa có template SLA nào'
                                )}
                            </p>
                        </div>
                    )}
                </Dialog>
            </Paper>
        )
    }

    // SLA is configured - show details
    const tier =
        sla.sla_template?.template_tier ||
        ('custom' as SLATier as Exclude<SLATier, 'custom'>)
    const tierConfig = tier && tier !== 'custom' ? SLA_TIER_CONFIG[tier] : null

    // Get response/resolution times from rules or template
    const slaRules = Array.isArray(sla.sla_rules) ? sla.sla_rules : []
    const defaultRule = slaRules.find((r) => r.ticket_type_group === 'default')
    const responseTime =
        defaultRule?.response_time_minutes ||
        sla.sla_template?.default_response_time
    const resolutionTime =
        defaultRule?.resolution_time_minutes ||
        sla.sla_template?.default_resolution_time

    return (
        <Paper className="p-4">
            {/* Header - Compact */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div
                        className="p-2 rounded-lg"
                        style={{
                            backgroundColor: tierConfig
                                ? `${tierConfig.color}20`
                                : '#dcfce7',
                        }}
                    >
                        <Shield
                            className="w-5 h-5"
                            style={{ color: tierConfig?.color || '#16a34a' }}
                        />
                    </div>
                    <div>
                        <div className="flex items-center gap-1.5">
                            <Typogaphy className="font-semibold text-gray-900 text-sm">
                                {t('sla.service_sla', 'Service SLA')}
                            </Typogaphy>
                            {sla.sla_template && tierConfig && (
                                <Badge
                                    className="text-[10px] h-5"
                                    style={{
                                        backgroundColor: tierConfig.color,
                                        color: 'white',
                                    }}
                                >
                                    {tier.toUpperCase()}
                                </Badge>
                            )}
                            {sla.is_active ? (
                                <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] h-5">
                                    <CheckCircle2 className="w-3 h-3 mr-0.5" />
                                    Active
                                </Badge>
                            ) : (
                                <Badge
                                    variant="outline"
                                    className="text-gray-500 text-[10px] h-5"
                                >
                                    Inactive
                                </Badge>
                            )}
                        </div>
                        <p className="text-xs text-gray-500">
                            {sla.sla_template?.template_name ||
                                SLA_SOURCE_LABELS[sla.sla_source]}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={goToSLAConfig}
                        className="h-7 text-xs"
                    >
                        <Settings className="w-3 h-3 mr-1" />
                        Sửa
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDelete}
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    >
                        <Trash2 className="w-3 h-3" />
                    </Button>
                </div>
            </div>

            {/* SLA Metrics - Compact Grid */}
            <div className="grid grid-cols-4 gap-2 mb-4">
                <div className="p-2 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-1 text-green-600 mb-1">
                        <TrendingUp className="w-3 h-3" />
                        <span className="text-[10px] font-medium">Uptime</span>
                    </div>
                    <p className="text-lg font-bold text-green-700">
                        {sla.uptime_guarantee ||
                            sla.sla_template?.uptime_guarantee}
                        %
                    </p>
                </div>
                <div className="p-2 rounded-lg bg-orange-50 border border-orange-200">
                    <div className="flex items-center gap-1 text-orange-600 mb-1">
                        <AlertTriangle className="w-3 h-3" />
                        <span className="text-[10px] font-medium">
                            Downtime
                        </span>
                    </div>
                    <p className="text-lg font-bold text-orange-700">
                        {sla.downtime_allowance_minutes ||
                            sla.sla_template?.downtime_allowance_minutes}
                        <span className="text-xs font-normal">m</span>
                    </p>
                </div>
                <div className="p-2 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-1 text-blue-600 mb-1">
                        <Clock className="w-3 h-3" />
                        <span className="text-[10px] font-medium">
                            Response
                        </span>
                    </div>
                    <p className="text-lg font-bold text-blue-700">
                        {responseTime || '-'}
                        <span className="text-xs font-normal">m</span>
                    </p>
                </div>
                <div className="p-2 rounded-lg bg-purple-50 border border-purple-200">
                    <div className="flex items-center gap-1 text-purple-600 mb-1">
                        <Timer className="w-3 h-3" />
                        <span className="text-[10px] font-medium">
                            Resolution
                        </span>
                    </div>
                    <p className="text-lg font-bold text-purple-700">
                        {resolutionTime || '-'}
                        <span className="text-xs font-normal">m</span>
                    </p>
                </div>
            </div>

            {/* SLA Rules - Compact Table */}
            {slaRules.length > 0 && (
                <div className="mb-3">
                    <p className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        SLA Rules ({slaRules.length})
                    </p>
                    <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-xs">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-2 py-1.5 text-left font-medium text-gray-600">
                                        Loại ticket
                                    </th>
                                    <th className="px-2 py-1.5 text-center font-medium text-gray-600">
                                        Priority
                                    </th>
                                    <th className="px-2 py-1.5 text-center font-medium text-gray-600">
                                        Response
                                    </th>
                                    <th className="px-2 py-1.5 text-center font-medium text-gray-600">
                                        Resolution
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {slaRules.map((rule, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-2 py-1.5 text-gray-700">
                                            {rule.ticket_type_group ===
                                            'default'
                                                ? 'Mặc định'
                                                : rule.ticket_type_key ||
                                                  rule.ticket_type_group}
                                        </td>
                                        <td className="px-2 py-1.5 text-center">
                                            {rule.priority ? (
                                                <Badge
                                                    variant="outline"
                                                    className="text-[10px] h-4 capitalize"
                                                >
                                                    {rule.priority}
                                                </Badge>
                                            ) : (
                                                <span className="text-gray-400">
                                                    -
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-2 py-1.5 text-center text-blue-600 font-medium">
                                            {rule.response_time_minutes}m
                                        </td>
                                        <td className="px-2 py-1.5 text-center text-purple-600 font-medium">
                                            {rule.resolution_time_minutes}m
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Additional Info - Compact */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 pt-3 border-t">
                <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>
                        {sla.effective_from}
                        {sla.effective_to && ` → ${sla.effective_to}`}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>
                        {sla.business_hours_only ||
                        sla.sla_template?.business_hours_only
                            ? 'Giờ HC'
                            : '24/7'}
                    </span>
                </div>
                {sla.business_hours_config &&
                    !sla.business_hours_config.include_weekends && (
                        <div className="flex items-center gap-1">
                            <span className="text-gray-400">•</span>
                            <span>
                                {sla.business_hours_config.work_hours?.start}-
                                {sla.business_hours_config.work_hours?.end}
                            </span>
                        </div>
                    )}
                {sla.escalation_rules && sla.escalation_rules.length > 0 && (
                    <Popover>
                        <PopoverTrigger>
                            <Badge
                                variant="outline"
                                className="text-[10px] h-5 cursor-pointer hover:bg-gray-100"
                            >
                                {sla.escalation_rules.length} cảnh báo
                            </Badge>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-3">
                            <div className="space-y-2">
                                <p className="text-xs font-semibold flex items-center gap-1 text-gray-700">
                                    <Bell className="w-3 h-3" />
                                    Cảnh báo SLA
                                </p>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {sla.escalation_rules.map((rule, idx) => (
                                        <div
                                            key={idx}
                                            className="p-2 bg-gray-50 rounded text-xs space-y-1"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium">
                                                    Cảnh báo #{idx + 1}
                                                </span>
                                                <span className="text-orange-600">
                                                    {
                                                        rule.condition
                                                            .no_response_after_minutes
                                                    }
                                                    m
                                                </span>
                                            </div>
                                            {rule.condition.priority &&
                                                rule.condition.priority.length >
                                                    0 && (
                                                    <div className="flex gap-1 flex-wrap">
                                                        {rule.condition.priority.map(
                                                            (p) => (
                                                                <Badge
                                                                    key={p}
                                                                    variant="outline"
                                                                    className="text-[9px] h-4 capitalize"
                                                                >
                                                                    {p}
                                                                </Badge>
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                            <div className="flex items-center gap-2 text-gray-500">
                                                {rule.notification_channels?.includes(
                                                    'email'
                                                ) && (
                                                    <Mail className="w-3 h-3" />
                                                )}
                                                {rule.notification_channels?.includes(
                                                    'sms'
                                                ) && (
                                                    <MessageSquare className="w-3 h-3" />
                                                )}
                                                {rule.escalate_to?.roles &&
                                                    rule.escalate_to.roles
                                                        .length > 0 && (
                                                        <span className="flex items-center gap-0.5">
                                                            <Users className="w-3 h-3" />
                                                            {rule.escalate_to.roles.join(
                                                                ', '
                                                            )}
                                                        </span>
                                                    )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
                {sla.custom_terms && sla.custom_terms.length > 0 && (
                    <Popover>
                        <PopoverTrigger>
                            <Badge
                                variant="outline"
                                className="text-[10px] h-5 cursor-pointer hover:bg-gray-100"
                            >
                                {sla.custom_terms.length} điều khoản
                            </Badge>
                        </PopoverTrigger>
                        <PopoverContent className="w-96 p-3">
                            <div className="space-y-2">
                                <p className="text-xs font-semibold flex items-center gap-1 text-gray-700">
                                    <FileText className="w-3 h-3" />
                                    Điều khoản tùy chỉnh
                                </p>
                                <div className="space-y-3 max-h-64 overflow-y-auto">
                                    {sla.custom_terms.map((term, idx) => (
                                        <div key={idx} className="space-y-1">
                                            <p className="text-xs font-medium text-gray-700">
                                                {term.key}
                                            </p>
                                            <TextEditorCustomize
                                                value={{
                                                    text: term.value || '',
                                                    attachments: [],
                                                }}
                                                disabled
                                                readOnly
                                                disabledAttachments
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
            </div>
        </Paper>
    )
}

export default ServiceSLAConfig
